const CCPA_CATEGORIES = [
  {
    code: "A",
    category: "Identifiers",
    examples:
      "Contact details, such as real name, alias, postal address, telephone or mobile contact number, unique personal identifier, online identifier, Internet Protocol address, email address, and account name",
    collected: "YES",
  },
  {
    code: "B",
    category: "Personal information as defined in the California Customer Records statute",
    examples:
      "Name, contact information, education, employment, employment history, and financial information",
    collected: "YES",
  },
  {
    code: "C",
    category: "Protected classification characteristics under state or federal law",
    examples:
      "Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data",
    collected: "YES",
  },
  {
    code: "D",
    category: "Commercial information",
    examples:
      "Transaction information, purchase history, financial details, and payment information",
    collected: "YES",
  },
  {
    code: "E",
    category: "Biometric information",
    examples: "Fingerprints and voiceprints",
    collected: "YES",
  },
  {
    code: "F",
    category: "Internet or other similar network activity",
    examples:
      "Browsing history, search history, online behavior, interest data, and interactions with our and other websites, applications, systems, and advertisements",
    collected: "YES",
  },
  {
    code: "G",
    category: "Geolocation data",
    examples: "Device location",
    collected: "YES",
  },
  {
    code: "H",
    category: "Audio, electronic, sensory, or similar information",
    examples:
      "Images and audio, video or call recordings created in connection with our business activities",
    collected: "YES",
  },
  {
    code: "I",
    category: "Professional or employment-related information",
    examples:
      "Business contact details in order to provide you our Services at a business level or job title, work history, and professional qualifications if you apply for a job with us",
    collected: "NO",
  },
  {
    code: "J",
    category: "Education Information",
    examples: "Student records and directory information",
    collected: "NO",
  },
  {
    code: "K",
    category: "Inferences drawn from collected personal information",
    examples:
      "Inferences drawn from any of the collected personal information listed above to create a profile or summary about, for example, an individual's preferences and characteristics",
    collected: "YES",
  },
  {
    code: "L",
    category: "Sensitive personal Information",
    examples:
      "Account login information, biometric data, contents of email or text messages, financial information including account access details, health data and precise geolocation",
    collected: "YES",
  },
];

export default function PrivacyPolicy() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-16 md:py-24 text-foreground">
      <header className="mb-12 border-b border-border pb-8">
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-3">
          Privacy Policy
        </h1>
        <p className="text-base text-muted-foreground">
          Last updated May 2, 2026
        </p>
      </header>

      <section className="mb-16">
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            This Privacy Notice for LegacyLens AI, Inc. (doing business as
            LegacyLens AI) ("we," "us," or "our"), describes how and why we
            might access, collect, store, use, and/or share ("process") your
            personal information when you use our services ("Services"),
            including when you visit our website at{" "}
            <a
              href="https://legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              https://legacylensai.com
            </a>{" "}
            or any website of ours that links to this Privacy Notice; use
            LegacyLens AI; or engage with us in other related ways, including
            any marketing or events.
          </p>
          <p>
            LegacyLens AI provides a digital legacy platform that captures and
            preserves voice recordings, likeness, and personal stories of
            consenting subjects, making them accessible to families,
            descendants, and visitors via QR-coded tributes. All content is
            created with the explicit, verifiable consent of the subject under
            the Digital Legacy Directive, which governs how voice, likeness,
            and memories are captured, preserved, and shared. We operate a B2B
            channel through cemeteries, funeral homes, and ancestry partners,
            alongside direct-to-family service.
          </p>
          <p>
            Questions or concerns? Reading this Privacy Notice will help you
            understand your privacy rights and choices. We are responsible for
            making decisions about how your personal information is processed.
            If you do not agree with our policies and practices, please do not
            use our Services. If you still have any questions or concerns,
            please contact us at{" "}
            <a
              href="mailto:privacy@legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              privacy@legacylensai.com
            </a>
            .
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Geographic Availability
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            LegacyLens AI currently offers its Services only to residents of
            the United States, and only to residents of states other than
            Illinois, Texas, and Washington. We do not offer the Services to
            residents of the European Union, the United Kingdom, Canada, or
            any other country, and we do not offer the Services to residents
            of Illinois, Texas, or Washington.
          </p>
          <p>
            This geographic limitation reflects our commitment to operating
            responsibly under applicable biometric privacy and data protection
            laws, including but not limited to the Illinois Biometric
            Information Privacy Act (BIPA), the Texas Capture or Use of
            Biometric Identifier Act (CUBI), the Washington Biometric Privacy
            Act and My Health My Data Act (MHMDA), the EU General Data
            Protection Regulation (GDPR), the United Kingdom General Data
            Protection Regulation (UK GDPR), and the Personal Information
            Protection and Electronic Documents Act (PIPEDA) in Canada. We are
            working toward expanded geographic availability and will update
            this Privacy Notice when the Services become available in
            additional jurisdictions.
          </p>
          <p>
            If you are a resident of an excluded jurisdiction and access this
            Privacy Notice or our website, we will not knowingly capture,
            process, or retain your voice, likeness, biographical content, or
            other Subject Content. If you believe Subject Content has been
            captured from a resident of an excluded jurisdiction in error,
            please contact us at{" "}
            <a
              href="mailto:privacy@legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              privacy@legacylensai.com
            </a>{" "}
            and we will delete the relevant content.
          </p>
        </div>
      </section>

      <section id="section-1" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          1. What Information Do We Collect?
        </h2>

        <h3 className="text-xl font-semibold mb-4">Personal information you disclose to us</h3>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: We collect personal information that you provide to us.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We collect personal information that you voluntarily provide to us
            when you register on the Services, express an interest in
            obtaining information about us or our products and Services, when
            you participate in activities on the Services, or otherwise when
            you contact us.
          </p>
          <p>
            <strong>Personal Information Provided by You.</strong> The
            personal information that we collect depends on the context of
            your interactions with us and the Services, the choices you make,
            and the products and features you use. The personal information
            we collect may include the following:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>names</li>
            <li>phone numbers</li>
            <li>email addresses</li>
            <li>passwords</li>
            <li>contact preferences</li>
            <li>billing addresses</li>
            <li>voice recordings</li>
            <li>likeness data including photographs and 3D scans</li>
            <li>biographical content and personal narratives</li>
            <li>voice clone models derived from voice recordings</li>
            <li>consent recordings (audio/video of subject reading consent statement)</li>
            <li>relationship between buyer and subject</li>
          </ul>
          <p>
            <strong>Sensitive Information.</strong> When necessary, with your
            consent or as otherwise permitted by applicable law, we process
            the following categories of sensitive information:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>biometric data</li>
            <li>financial data</li>
            <li>voice clone models derived from biometric voice recordings</li>
            <li>cryptographically bound consent recordings (audio/video of subject reading consent statement)</li>
            <li>post-mortem identity data of deceased subjects, including voice, likeness, and biographical narratives</li>
            <li>health data</li>
          </ul>
          <p>
            <strong>Payment Data.</strong> We may collect data necessary to
            process your payment if you choose to make purchases, such as
            your payment instrument number, and the security code associated
            with your payment instrument. All payment data is handled and
            stored by Stripe. You may find their privacy notice link(s)
            here:{" "}
            <a
              href="https://stripe.com/privacy"
              className="font-medium underline-offset-4 hover:underline"
            >
              https://stripe.com/privacy
            </a>
            .
          </p>
          <p>
            Payments on the LegacyLens platform are processed exclusively by
            Stripe, Inc. We do not collect, store, or transmit full payment
            card numbers, security codes, or bank account credentials
            directly; this information is collected and processed by Stripe
            under their privacy policy and security standards (PCI DSS Level
            1). Our platform supports two distinct payment flows: (1)
            one-time commissioning fees paid by Buyers when ordering a
            Digital Legacy, and (2) per-access fees paid by visitors who scan
            a QR code to access a tribute. Per-access fees are distributed
            among the Buyer who commissioned the tribute, LegacyLens, the
            partner institution hosting the tribute (such as a cemetery,
            funeral home, or ancestry partner), and a charitable cause, in a
            fixed split disclosed in our Terms of Service. Refund eligibility
            is governed by our Terms of Service. Revocation of consent for a
            Digital Legacy does not, by itself, entitle the Subject or Buyer
            to a refund of fees previously paid, except as required by
            applicable law.
          </p>
          <p>
            All personal information that you provide to us must be true,
            complete, and accurate, and you must notify us of any changes to
            such personal information.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-10 mb-4">Information automatically collected</h3>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: Some information — such as your Internet Protocol (IP)
          address and/or browser and device characteristics — is collected
          automatically when you visit our Services.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We automatically collect certain information when you visit, use,
            or navigate the Services. This information does not reveal your
            specific identity (like your name or contact information) but may
            include device and usage information, such as your IP address,
            browser and device characteristics, operating system, language
            preferences, referring URLs, device name, country, location,
            information about how and when you use our Services, and other
            technical information. This information is primarily needed to
            maintain the security and operation of our Services, and for our
            internal analytics and reporting purposes.
          </p>
          <p>
            Like many businesses, we also collect information through cookies
            and similar technologies. You can find out more about this in our{" "}
            <a
              href="/cookie-policy"
              className="font-medium underline-offset-4 hover:underline"
            >
              Cookie Notice
            </a>
            .
          </p>
          <p>The information we collect includes:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Log and Usage Data.</strong> Service-related, diagnostic,
              usage, and performance information our servers automatically
              collect when you access or use our Services.
            </li>
            <li>
              <strong>Device Data.</strong> Information about your computer,
              phone, tablet, or other device you use to access the Services.
            </li>
            <li>
              <strong>Location Data.</strong> Information about your device's
              location, which can be either precise or imprecise.
            </li>
            <li>
              <strong>AR rendering and capability data.</strong> Information
              about how augmented reality content is delivered to and
              displayed on the user's device.
            </li>
            <li>
              <strong>QR scan event metadata.</strong> Information generated
              when a user scans a QR code linked to a tribute on the
              LegacyLens platform, including timestamp, tribute identifier,
              and partner institution context.
            </li>
            <li>
              <strong>Tribute interaction data.</strong> Information about how
              a user interacts with a tribute page during a session, including
              voice conversation session metadata such as duration, message
              count, and any safety-guardrail events triggered. This category
              does not include the content of voice conversations themselves,
              which are processed ephemerally and not retained as transcripts.
            </li>
          </ul>
        </div>
      </section>

      <section id="section-2" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          2. How Do We Process Your Information?
        </h2>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: We process your information to provide, improve, and
          administer our Services, communicate with you, for security and
          fraud prevention, and to comply with law.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We process the personal information for the following purposes
            listed below. We may also process your information for other
            purposes only with your prior explicit consent.
          </p>
          <p>
            We process your personal information for a variety of reasons,
            depending on how you interact with our Services, including:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>To facilitate account creation and authentication and otherwise manage user accounts.</strong>
            </li>
            <li>
              <strong>To deliver and facilitate delivery of services to the user.</strong>
            </li>
            <li>
              <strong>To respond to user inquiries and offer support to users.</strong>
            </li>
            <li>
              <strong>To send administrative information to you</strong> about
              our products and services, changes to our terms and policies,
              and similar communications.
            </li>
            <li>
              <strong>To fulfill and manage your orders,</strong> payments,
              returns, and exchanges made through the Services.
            </li>
            <li>
              <strong>To enable user-to-user communications.</strong>
            </li>
            <li>
              <strong>To protect our Services,</strong> including fraud
              monitoring and prevention.
            </li>
            <li>
              <strong>To identify usage trends</strong> so we can improve our
              Services.
            </li>
            <li>
              <strong>To save or protect an individual's vital interest,</strong>{" "}
              such as to prevent harm.
            </li>
            <li>
              <strong>To enable Subjects to authorize and manage their Digital Legacy.</strong>{" "}
              The Subject is the only party who can authorize the creation of
              their Digital Legacy on our platform.
            </li>
            <li>
              <strong>To create and maintain verifiable consent records,</strong>{" "}
              including audio or video recordings of the Subject,
              cryptographically bound to the moment of authorization.
            </li>
            <li>
              <strong>To preserve Subject Content in perpetuity,</strong> with
              architectural redundancy across independent storage systems, in
              accordance with the preservation commitments and
              platform-transition obligations described in our Terms of
              Service.
            </li>
            <li>
              <strong>To facilitate revocation of consent and posthumous action by executors,</strong>{" "}
              within the scope the Subject originally authorized.
            </li>
            <li>
              <strong>To operate access fees and partner revenue arrangements,</strong>{" "}
              including reconciliation with cemeteries, funeral homes, and
              ancestry partners.
            </li>
            <li>
              <strong>To monitor and enforce platform safety against misuse of the AI avatar,</strong>{" "}
              including detection of attempts to elicit content the Subject
              did not authorize.
            </li>
            <li>
              <strong>To verify executor authority for posthumous actions,</strong>{" "}
              including review of identity documentation and evidence of
              legal authority.
            </li>
            <li>
              <strong>To maintain audit records of consent and revocation events,</strong>{" "}
              for a period necessary to demonstrate compliance with our
              obligations.
            </li>
            <li>
              <strong>To preserve platform continuity in the event of business transition,</strong>{" "}
              including providing notice and a retrieval window to Subjects
              and their estates as described in our Terms of Service.
            </li>
          </ul>
        </div>
      </section>

      <section id="section-3" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          3. What Legal Bases Do We Rely On to Process Your Information?
        </h2>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: We only process your personal information when we believe
          it is necessary and we have a valid legal reason to do so under
          applicable law.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Because the Services are not currently offered to residents of the
            European Union, the United Kingdom, Switzerland, or Canada (see
            Geographic Availability above), the legal-bases provisions of
            GDPR, UK GDPR, Swiss FADP, and Canadian PIPEDA do not currently
            govern our processing of personal information from Service users.
            We are providing the following information for completeness and
            as a record of our intended approach if and when we expand to
            those jurisdictions.
          </p>
          <p>
            We may rely on the following legal bases under GDPR or UK GDPR if
            and when we expand into those jurisdictions: consent, performance
            of a contract, legitimate interests, legal obligations, and vital
            interests. Under PIPEDA in Canada, we may rely on express or
            implied consent, and on the limited statutory exceptions that
            permit processing without consent.
          </p>
        </div>
      </section>

      <section id="section-4" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          4. When and With Whom Do We Share Your Personal Information?
        </h2>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: We may share information in specific situations described
          in this section and/or with the following categories of third
          parties.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            <strong>Vendors, Consultants, and Other Third-Party Service Providers.</strong>{" "}
            We may share your data with third-party vendors, service
            providers, contractors, or agents who perform services for us or
            on our behalf and require access to such information to do that
            work. We have contracts in place with our third parties, which
            are designed to help safeguard your personal information.
          </p>
          <p>
            The categories of third parties we may share personal information
            with are as follows:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>AI Platforms</li>
            <li>Cloud Computing Services</li>
            <li>Data Storage Service Providers</li>
            <li>Government Entities</li>
            <li>Payment Processors</li>
            <li>Performance Monitoring Tools</li>
            <li>User Account Registration & Authentication Services</li>
            <li>Website Hosting Service Providers</li>
            <li>
              <strong>Voice synthesis and voice cloning service providers</strong>{" "}
              — we disclose voice recordings and voice clone models to
              third-party voice synthesis providers solely for the purpose of
              generating the Subject's authorized Digital Legacy.
            </li>
            <li>
              <strong>Partner institutions hosting tributes</strong> —
              including cemeteries, funeral homes, and ancestry partners. The
              information disclosed is limited to what is necessary to
              install, maintain, and reconcile access fees for tributes
              hosted at the partner institution. We do not disclose Subject
              Content or consent recordings to partner institutions.
            </li>
            <li>
              <strong>Designated executors of deceased Subjects</strong> —
              upon verification of the executor's authority, solely to enable
              the executor to act on the Subject's previously stated wishes
              within the scope the Subject originally authorized.
            </li>
            <li>
              <strong>Transactional email service providers</strong> — solely
              for the purpose of delivering order confirmations, capture
              session reminders, tribute completion notifications, account
              security alerts, and other administrative communications.
            </li>
          </ul>
          <p>
            We also may need to share your personal information in the
            following situations:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Business Transfers.</strong> We may share or transfer
              your information in connection with, or during negotiations of,
              any merger, sale of company assets, financing, or acquisition
              of all or a portion of our business to another company. Any
              such transfer is subject to the platform-transition obligations
              described in Section 27 of our Terms of Service, which require
              any acquirer to honor the Digital Legacy Directive or provide
              notice and retrieval to Subjects.
            </li>
            <li>
              <strong>Business Partners.</strong> We may share your
              information with our business partners to offer you certain
              products, services, or promotions.
            </li>
          </ul>
        </div>
      </section>

      <section id="section-5" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          5. Do We Use Cookies and Other Tracking Technologies?
        </h2>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: We use only strictly necessary cookies for authentication
          and security. We do not use cookies for advertising, analytics, or
          behavioral tracking.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We use cookies and similar technologies only where they are
            required for technical reasons in order for our Services to
            operate, including user authentication, session management, and
            security. We do not use cookies for advertising, analytics, or
            marketing, and we do not allow third parties to serve advertising
            cookies through our Services. Specific information about which
            cookies we use, and how you can refuse certain cookies through
            your browser, is set out in our{" "}
            <a
              href="/cookie-policy"
              className="font-medium underline-offset-4 hover:underline"
            >
              Cookie Notice
            </a>
            .
          </p>
        </div>
      </section>

      <section id="section-6" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          6. Do We Offer Artificial Intelligence-Based Products?
        </h2>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: We offer products, features, or tools powered by
          artificial intelligence, machine learning, or similar technologies.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            As part of our Services, we offer products, features, or tools
            powered by artificial intelligence, machine learning, or similar
            technologies (collectively, "AI Products"). These tools are
            designed to enhance your experience and provide you with
            innovative solutions.
          </p>
          <p>
            We provide the AI Products through third-party service providers
            ("AI Service Providers"), including ElevenLabs, Anthropic, and
            OpenAI. As outlined in this Privacy Notice, your input, output,
            and personal information will be shared with and processed by
            these AI Service Providers to enable your use of our AI Products.
          </p>
          <p>
            Our AI Products are designed for the following functions: AI bots
            and natural language processing.
          </p>
          <p>
            <strong>How to Opt Out.</strong> Subjects may opt out of AI
            processing of their voice and likeness at any time by exercising
            their revocation rights under Section 25.5 of our Terms of
            Service. Visitors to tribute pages who do not wish to interact
            with the AI avatar may simply not initiate or end any
            conversation; the avatar does not initiate contact and does not
            retain conversation content as transcripts.
          </p>
        </div>
      </section>

      <section id="section-7" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          7. Is Your Information Transferred Internationally?
        </h2>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: We process information in the United States. The Services
          are not currently offered to residents outside the United States.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Our servers are located in the United States, and the Services
            are currently offered only to residents of the United States as
            described in the Geographic Availability section above. Personal
            information collected through the Services is processed in our
            facilities and in the facilities of the third parties with whom
            we may share your personal information, substantially all of
            which are located in the United States.
          </p>
          <p>
            If we expand the Services to residents of the European Economic
            Area (EEA), the United Kingdom (UK), Switzerland, Canada, or
            other jurisdictions in the future, we will update this Privacy
            Notice and implement appropriate safeguards for any cross-border
            transfers of personal information, including, where required, the
            European Commission's Standard Contractual Clauses or equivalent
            mechanisms.
          </p>
        </div>
      </section>

      <section id="section-8" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          8. How Long Do We Keep Your Information?
        </h2>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: We keep your information for as long as necessary to
          fulfill the purposes outlined in this Privacy Notice unless
          otherwise required by law. Subject Content is preserved in
          perpetuity in connection with the authorized Digital Legacy,
          subject to revocation rights.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We will only keep your personal information for as long as it is
            necessary for the purposes set out in this Privacy Notice, unless
            a longer retention period is required or permitted by law (such
            as tax, accounting, or other legal requirements). Subject Content
            (including voice recordings, likeness captures, biographical
            content, voice clone models, and consent recordings) is preserved
            in perpetuity in connection with the Subject's Digital Legacy,
            subject to the Subject's revocation rights described in Section
            25.5 of our Terms of Service and the platform-transition
            obligations described in Section 27. Upon revocation by a
            Subject during their lifetime, Subject Content is permanently
            removed from our platform within thirty (30) days, except for
            consent records, which we may retain in suspended state for up
            to ten (10) years for audit and legal purposes.
          </p>
          <p>
            When we have no ongoing legitimate business need to process your
            personal information, we will either delete or anonymize such
            information, or, if this is not possible (for example, because
            your personal information has been stored in backup archives),
            then we will securely store your personal information and
            isolate it from any further processing until deletion is
            possible.
          </p>
        </div>
      </section>

      <section id="section-9" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          9. How Do We Keep Your Information Safe?
        </h2>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: We aim to protect your personal information through a
          system of organizational and technical security measures.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We have implemented appropriate and reasonable technical and
            organizational security measures designed to protect the
            security of any personal information we process. However,
            despite our safeguards and efforts to secure your information,
            no electronic transmission over the Internet or information
            storage technology can be guaranteed to be 100% secure, so we
            cannot promise or guarantee that hackers, cybercriminals, or
            other unauthorized third parties will not be able to defeat our
            security and improperly collect, access, steal, or modify your
            information. Although we will do our best to protect your
            personal information, transmission of personal information to
            and from our Services is at your own risk. You should only
            access the Services within a secure environment.
          </p>
        </div>
      </section>

      <section id="section-10" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          10. Do We Collect Information From Minors?
        </h2>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: We do not knowingly collect data from or market to
          children under 18 years of age.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We do not knowingly collect, solicit data from, or market to
            children under 18 years of age, nor do we knowingly sell such
            personal information. By using the Services, you represent that
            you are at least 18 years of age. We do not permit minors to be
            Subjects of Digital Legacies on our platform under any
            circumstances. If we learn that personal information from users
            less than 18 years of age has been collected, we will deactivate
            the account and take reasonable measures to promptly delete such
            data from our records. If you become aware of any data we may
            have collected from children under age 18, please contact us at{" "}
            <a
              href="mailto:privacy@legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              privacy@legacylensai.com
            </a>
            .
          </p>
        </div>
      </section>

      <section id="section-11" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          11. What Are Your Privacy Rights?
        </h2>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: Depending on your state of residence, applicable privacy
          law may give you rights regarding your personal information. You
          may review, change, or terminate your account at any time.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            In some regions you have certain rights under applicable data
            protection laws. These may include the right (i) to request
            access and obtain a copy of your personal information, (ii) to
            request rectification or erasure; (iii) to restrict the
            processing of your personal information; (iv) if applicable, to
            data portability; and (v) not to be subject to automated
            decision-making. In certain circumstances, you may also have the
            right to object to the processing of your personal information.
            You can make such a request by contacting us using the contact
            details below.
          </p>
          <p>
            <strong>Withdrawing your consent:</strong> If we are relying on
            your consent to process your personal information, you have the
            right to withdraw your consent at any time by contacting us, or,
            in the case of consent for a Digital Legacy, by exercising your
            revocation rights under Section 25.5 of our Terms of Service.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-10 mb-4">Account Information</h3>
        <p className="text-lg leading-relaxed mb-4">
          If you would at any time like to review or change the information
          in your account or terminate your account, you can contact us using
          the contact information provided, or log in to your account
          settings and update your user account.
        </p>
        <p className="text-lg leading-relaxed">
          Upon your request to terminate your account, we will deactivate or
          delete your account and information from our active databases.
          However, we may retain some information in our files to prevent
          fraud, troubleshoot problems, assist with any investigations,
          enforce our legal terms, and/or comply with applicable legal
          requirements.
        </p>
      </section>

      <section id="section-12" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          12. Controls for Do-Not-Track Features
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Most web browsers and some mobile operating systems and mobile
            applications include a Do-Not-Track ("DNT") feature or setting
            you can activate to signal your privacy preference not to have
            data about your online browsing activities monitored and
            collected. At this stage, no uniform technology standard for
            recognizing and implementing DNT signals has been finalized. As
            such, we do not currently respond to DNT browser signals or any
            other mechanism that automatically communicates your choice not
            to be tracked online.
          </p>
          <p>
            California law requires us to let you know how we respond to web
            browser DNT signals. Because there currently is not an industry
            or legal standard for recognizing or honoring DNT signals, we do
            not respond to them at this time.
          </p>
        </div>
      </section>

      <section id="section-13" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          13. Do United States Residents Have Specific Privacy Rights?
        </h2>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: If you are a resident of California, Colorado,
          Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland,
          Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon,
          Rhode Island, Tennessee, Utah, or Virginia, you may have specific
          privacy rights. The Services are not currently offered to residents
          of Illinois, Texas, or Washington; we do not address rights under
          those states' laws here.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            You may have the right to request access to and receive details
            about the personal information we maintain about you and how we
            have processed it, correct inaccuracies, get a copy of, or
            delete your personal information. You may also have the right
            to withdraw your consent to our processing of your personal
            information. These rights may be limited in some circumstances
            by applicable law.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-10 mb-4">Categories of Personal Information We Collect</h3>
        <p className="text-lg leading-relaxed mb-4">
          The table below shows the categories of personal information we
          have collected in the past twelve (12) months.
        </p>

        <div className="overflow-x-auto mb-8">
          <table className="w-full text-base border-collapse border border-border">
            <thead className="bg-muted">
              <tr>
                <th className="border border-border p-3 text-left font-semibold">Category</th>
                <th className="border border-border p-3 text-left font-semibold">Examples</th>
                <th className="border border-border p-3 text-left font-semibold">Collected</th>
              </tr>
            </thead>
            <tbody>
              {CCPA_CATEGORIES.map((cat) => (
                <tr key={cat.code}>
                  <td className="border border-border p-3 align-top">
                    <strong>{cat.code}.</strong> {cat.category}
                  </td>
                  <td className="border border-border p-3 align-top">{cat.examples}</td>
                  <td className="border border-border p-3 align-top font-semibold">{cat.collected}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-lg leading-relaxed mb-5">
          We only collect sensitive personal information, as defined by
          applicable privacy laws or the purposes allowed by law or with
          your consent. Sensitive personal information may be used, or
          disclosed to a service provider or contractor, for additional,
          specified purposes. You may have the right to limit the use or
          disclosure of your sensitive personal information. We do not
          collect or process sensitive personal information for the purpose
          of inferring characteristics about you.
        </p>

        <h3 className="text-xl font-semibold mt-10 mb-4">Retention by Category</h3>
        <div className="space-y-4 text-lg leading-relaxed">
          <p>
            <strong>Category A — Identifiers.</strong> Identifiers associated
            with a Subject's Digital Legacy are preserved in perpetuity in
            connection with the tribute, subject to the Subject's revocation
            rights described in Section 25.5 of our Terms of Service and the
            platform-transition obligations described in Section 27. For
            Buyers, identifiers are retained for the duration of the Buyer's
            account plus a reasonable period thereafter for tax, legal, and
            operational record-keeping. Consent records associated with
            revoked Subject Content may be retained in suspended state for
            up to ten (10) years for audit and legal purposes.
          </p>
          <p>
            <strong>Category B — Personal information as defined in the California Customer Records statute.</strong>{" "}
            Subject Content is preserved in perpetuity, subject to the
            Subject's revocation rights described in Section 25.5 of our
            Terms of Service and the platform-transition obligations
            described in Section 27. Following revocation by a Subject
            during their lifetime, Subject Content is permanently removed
            from our platform within thirty (30) days, except for consent
            records, which we may retain in suspended state for up to ten
            (10) years for audit and legal purposes.
          </p>
          <p>
            <strong>Category C — Protected classification characteristics.</strong>{" "}
            For Subjects, characteristics included in the Digital Legacy are
            preserved in perpetuity in connection with the tribute, subject
            to the Subject's revocation rights described in Section 25.5
            and the platform-transition obligations described in Section 27.
            For Buyers, characteristics associated with the account are
            retained for the duration of the Buyer's account plus a
            reasonable period thereafter.
          </p>
          <p>
            <strong>Category D — Commercial information.</strong> Consumer
            data, including purchase history and transaction records, is
            retained for the duration of the Buyer's account plus a
            reasonable period thereafter (typically seven to ten years) to
            comply with tax, accounting, and legal record-keeping
            requirements. Transaction records associated with tributes
            preserved in perpetuity are retained for the lifetime of the
            tribute.
          </p>
          <p>
            <strong>Category E — Biometric information.</strong> Biometric
            information, including voice recordings, voice clone models, 3D
            scans, photographs, and cryptographically bound consent
            recordings, is retained according to its purpose under our
            Digital Legacy Directive. Biometric information that is part of
            a Subject's authorized Digital Legacy is preserved in perpetuity
            as the core function of the tribute, subject to the Subject's
            revocation rights described in Section 25.5 and the
            platform-transition obligations described in Section 27.
            Following revocation by a Subject during their lifetime,
            biometric information is permanently removed from our platform
            within thirty (30) days, except for the consent recording
            itself, which we may retain in suspended state for up to ten
            (10) years for audit and legal purposes. Biometric information
            is processed under explicit, verifiable consent from the
            Subject, in compliance with applicable biometric privacy laws
            in the jurisdictions where the Services are offered.
          </p>
          <p>
            <strong>Category F — Internet or other similar network activity.</strong>{" "}
            Internet activity data, including server logs, QR scan event
            metadata, AR rendering data, and tribute interaction metadata,
            is retained for periods appropriate to the purpose for which it
            is collected. Operational server logs are typically retained for
            30 to 90 days. QR scan and access fee event records are
            retained for the lifetime of the associated tribute. Voice
            conversation session metadata is retained as ephemeral session
            data and is not stored as transcripts or content.
          </p>
          <p>
            <strong>Category G — Geolocation data.</strong> Geolocation data
            is retained for periods appropriate to the source and purpose.
            IP-based geolocation captured in server logs is retained for 30
            to 90 days. QR scan event location context is retained for the
            lifetime of the associated tribute. Billing address location
            data is retained for the duration of the Buyer's account plus a
            reasonable period thereafter (typically seven to ten years).
          </p>
          <p>
            <strong>Category H — Audio, electronic, sensory, or similar information.</strong>{" "}
            Sensory data, including voice recordings, photographs, video
            recordings, 3D scans, and verifiable consent recordings, is
            preserved in perpetuity in connection with the Subject's
            Digital Legacy, subject to the Subject's revocation rights
            described in Section 25.5 and the platform-transition
            obligations described in Section 27. Following revocation by a
            Subject during their lifetime, sensory data associated with the
            revoked Digital Legacy is permanently removed from our platform
            within thirty (30) days, except for consent recordings, which
            we may retain in suspended state for up to ten (10) years for
            audit and legal purposes. Visitor voice input during AI avatar
            conversations is processed ephemerally and is not retained as
            recordings or transcripts.
          </p>
          <p>
            <strong>Category K — Inferences.</strong> Inferences derived
            from a Subject's personal information, including the Subject's
            voice clone model and the inference-based responses generated
            by the AI avatar, are preserved in perpetuity in connection
            with the Subject's Digital Legacy, subject to the Subject's
            revocation rights described in Section 25.5 and the
            platform-transition obligations described in Section 27.
            Inferences generated for safety and guardrail purposes are
            retained as aggregated metadata for security monitoring and may
            be retained for up to ten (10) years for audit and legal
            purposes. Following revocation by a Subject during their
            lifetime, inference models and content derived from the
            Subject's data are permanently removed from our platform within
            thirty (30) days.
          </p>
          <p>
            <strong>Category L — Sensitive personal information.</strong>{" "}
            Sensitive personal information, including biometric data, health
            information, and contents of communications, is preserved in
            perpetuity in connection with the Subject's Digital Legacy,
            subject to the Subject's revocation rights described in Section
            25.5 and the platform-transition obligations described in
            Section 27. Following revocation by a Subject during their
            lifetime, sensitive personal information associated with the
            revoked Digital Legacy is permanently removed from our platform
            within thirty (30) days, except for consent recordings, which
            we may retain in suspended state for up to ten (10) years.
            Account login information and financial information are
            retained for the duration of the Buyer's account plus a
            reasonable period thereafter (typically seven to ten years).
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-10 mb-4">Your Rights</h3>
        <p className="text-lg leading-relaxed mb-4">
          You have rights under certain US state data protection laws.
          However, these rights are not absolute, and in certain cases, we
          may decline your request as permitted by law. These rights
          include:
        </p>
        <ul className="list-disc pl-6 space-y-1 text-lg leading-relaxed">
          <li>Right to know whether or not we are processing your personal data</li>
          <li>Right to access your personal data</li>
          <li>Right to correct inaccuracies in your personal data</li>
          <li>Right to request the deletion of your personal data</li>
          <li>Right to obtain a copy of the personal data you previously shared with us</li>
          <li>Right to non-discrimination for exercising your rights</li>
          <li>Right to opt out of the processing of your personal data if it is used for targeted advertising, the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects</li>
        </ul>

        <h3 className="text-xl font-semibold mt-10 mb-4">How to Exercise Your Rights</h3>
        <p className="text-lg leading-relaxed">
          To exercise these rights, you can contact us by emailing us at{" "}
          <a
            href="mailto:privacy@legacylensai.com"
            className="font-medium underline-offset-4 hover:underline"
          >
            privacy@legacylensai.com
          </a>
          , by visiting{" "}
          <a
            href="/contact"
            className="font-medium underline-offset-4 hover:underline"
          >
            legacylensai.com/contact
          </a>
          , or by referring to the contact details at the bottom of this
          document.
        </p>

        <h3 className="text-xl font-semibold mt-10 mb-4">Appeals</h3>
        <p className="text-lg leading-relaxed">
          Under certain US state data protection laws, if we decline to take
          action regarding your request, you may appeal our decision by
          emailing us at{" "}
          <a
            href="mailto:privacy@legacylensai.com"
            className="font-medium underline-offset-4 hover:underline"
          >
            privacy@legacylensai.com
          </a>
          . We will inform you in writing of any action taken or not taken
          in response to the appeal.
        </p>
      </section>

      <section id="section-14" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          14. Our Commitment to the Digital Legacy Directive
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            LegacyLens AI is the author and adopter of the Digital Legacy
            Directive, a public, versioned framework that governs how a
            person's voice, likeness, and memories may be captured,
            preserved, and shared with their consent. Our processing of
            personal information operates under the principles of the
            Directive, including:
          </p>
          <p>
            <strong>Subject-only consent.</strong> No Digital Legacy is
            created on our platform without the personal authorization of
            the Subject. No third party — including a family member,
            employer, or legal representative acting under power of
            attorney — may consent to the creation of a Digital Legacy on
            the Subject's behalf during the Subject's lifetime.
          </p>
          <p>
            <strong>Verifiable consent.</strong> Every consent record we
            store includes an audio or video recording of the Subject
            reading aloud a consent statement, cryptographically bound to
            the moment of authorization, so that consent cannot be disputed
            or fabricated.
          </p>
          <p>
            <strong>No commercial exploitation outside the legacy's intent.</strong>{" "}
            We do not use Subject Content to train commercial AI models
            other than the Subject's own Digital Legacy, do not license
            Subject Content for advertising, and do not sell or share
            Subject Content with third parties for purposes outside the
            authorized intent of the Digital Legacy.
          </p>
          <p>
            <strong>Preservation in perpetuity, with portability.</strong>{" "}
            We commit to preserving Subject Content indefinitely, subject to
            the Subject's revocation rights and our platform-transition
            obligations. If we cease operations or are acquired, Subjects
            and their estates will receive a minimum of 180 days' advance
            notice before any content is permanently removed, and a minimum
            of 365 days from the date of notice during which they may
            retrieve their Digital Legacy in a portable, usable format.
          </p>
          <p>
            The full text of the Digital Legacy Directive, including its
            current version and all prior versions, is available at{" "}
            <a
              href="/digital-legacy-directive"
              className="font-medium underline-offset-4 hover:underline"
            >
              legacylensai.com/digital-legacy-directive
            </a>
            . Where any provision of this Privacy Policy conflicts with the
            Digital Legacy Directive or our Terms of Service, the document
            providing greater protection to the Subject controls.
          </p>
        </div>
      </section>

      <section id="section-15" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          15. Do We Make Updates to This Notice?
        </h2>
        <p className="text-base italic text-muted-foreground mb-4">
          In Short: Yes, we will update this notice as necessary to stay
          compliant with relevant laws.
        </p>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We may update this Privacy Notice from time to time. The updated
            version will be indicated by an updated "Last updated" date at
            the top of this Privacy Notice. If we make material changes to
            this Privacy Notice, we may notify you either by prominently
            posting a notice of such changes or by directly sending you a
            notification. We encourage you to review this Privacy Notice
            frequently to be informed of how we are protecting your
            information.
          </p>
        </div>
      </section>

      <section id="section-16" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          16. How Can You Contact Us About This Notice?
        </h2>
        <p className="text-lg leading-relaxed mb-5">
          If you have questions or comments about this notice, you may email
          us at{" "}
          <a
            href="mailto:privacy@legacylensai.com"
            className="font-medium underline-offset-4 hover:underline"
          >
            privacy@legacylensai.com
          </a>{" "}
          or contact us by post at:
        </p>
        <address className="not-italic font-semibold text-lg leading-relaxed">
          <div>LegacyLens AI, Inc.</div>
          <div>447 Broadway, 2nd Floor #1240</div>
          <div>New York, NY 10013</div>
          <div>United States</div>
        </address>
      </section>

      <section id="section-17" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          17. How Can You Review, Update, or Delete the Data We Collect From You?
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Based on the applicable laws of your country or state of
            residence in the US, you may have the right to request access to
            the personal information we collect from you, details about how
            we have processed it, correct inaccuracies, or delete your
            personal information. You may also have the right to withdraw
            your consent to our processing of your personal information.
            These rights may be limited in some circumstances by applicable
            law. To request to review, update, or delete your personal
            information, please email us at{" "}
            <a
              href="mailto:privacy@legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              privacy@legacylensai.com
            </a>
            .
          </p>
        </div>
      </section>
    </main>
  );
}
